import React from 'react';
import { Clock } from 'lucide-react';
import { cn } from './cn';
import StatusPill from './StatusPill';

/**
 * ScorecardBookingCard — the signature booking card, styled like a cricket
 * scorecard: a green accent rail, the customer/team as the "batter" line, the
 * service + ground as context, a time row, and a footer that pairs the amount
 * with a payment status. All colours come from design tokens.
 *
 * Props:
 *  - title:          customer / team name (headline)
 *  - avatar:         optional node rendered in the leading circle (else initials)
 *  - initials:       fallback text for the leading circle (derived from title if omitted)
 *  - service:        service name (e.g. "Turf A · Cricket")
 *  - ground:         ground / court label
 *  - time:           date + time string
 *  - status:         booking status key -> StatusPill
 *  - statusLabel:    optional override for the status pill text
 *  - amount:         formatted amount (e.g. "₹1,200")
 *  - paymentStatus:  payment status key -> footer StatusPill
 *  - onClick:        makes the card interactive
 *  - actions:        optional node rendered in the footer (buttons)
 */
const initialsFrom = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'GA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export default function ScorecardBookingCard({
  title,
  avatar,
  initials,
  service,
  ground,
  time,
  status,
  statusLabel,
  amount,
  paymentStatus,
  onClick,
  actions,
  className,
  children,
  ...props
}) {
  const Wrapper = onClick ? 'button' : 'div';
  const context = [service, ground].filter(Boolean).join(' · ');
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'relative w-full overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface text-left shadow-ga-soft',
        'before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-ga-primary before:content-[""]',
        onClick && 'transition-colors hover:border-ga-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ga-focus',
        className
      )}
      {...props}
    >
      <div className="p-4 pl-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ga-full bg-ga-primary-soft text-sm font-bold text-ga-primary">
              {avatar || initials || initialsFrom(title)}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-bold text-ga-text-primary">{title}</div>
              {context && <div className="truncate text-[12px] text-ga-text-muted">{context}</div>}
            </div>
          </div>
          {status && <StatusPill status={status} label={statusLabel} size="sm" className="shrink-0" />}
        </div>

        {time && (
          <div className="mt-3 flex items-center gap-1.5 text-[12px] text-ga-text-muted">
            <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{time}</span>
          </div>
        )}

        {children}

        {(amount != null || paymentStatus || actions) && (
          <div className="mt-3 flex items-center justify-between gap-3 border-t border-ga-border-subtle pt-3">
            <div className="flex items-center gap-2">
              {amount != null && <span className="text-base font-bold text-ga-text-primary">{amount}</span>}
              {paymentStatus && <StatusPill status={paymentStatus} size="sm" />}
            </div>
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </div>
        )}
      </div>
    </Wrapper>
  );
}
